export type UnitSystem = "metric" | "imperial";

const KM_TO_MI = 0.621371;
const M_TO_FT = 3.28084;
const MPS_TO_MPH = 2.23694;
const MPS_TO_KPH = 3.6;

const STORAGE_KEY = "fitconvert-units";

export function getDefaultUnitSystem(): UnitSystem {
  if (typeof window === "undefined") return "metric";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "metric" || stored === "imperial") return stored;
  const lang = navigator.language || "";
  return lang === "en-US" || lang === "en-LR" || lang === "my-MM" ? "imperial" : "metric";
}

export function saveUnitSystem(system: UnitSystem): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, system);
  }
}

export function formatDistance(meters: number, system: UnitSystem): string {
  if (system === "imperial") {
    const miles = meters / 1000 * KM_TO_MI;
    if (miles >= 0.1) return `${miles.toFixed(1)} mi`;
    const feet = meters * M_TO_FT;
    return `${Math.round(feet)} ft`;
  }
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
}

export function formatPace(durationMs: number, distanceM: number, system: UnitSystem): string {
  if (distanceM <= 0) return "—";
  const totalSec = durationMs / 1000;
  if (system === "imperial") {
    const miles = distanceM / 1000 * KM_TO_MI;
    const paceSecPerMi = totalSec / miles;
    const m = Math.floor(paceSecPerMi / 60);
    const s = Math.round(paceSecPerMi % 60);
    return `${m}:${String(s).padStart(2, "0")} /mi`;
  }
  const paceSecPerKm = totalSec / (distanceM / 1000);
  const m = Math.floor(paceSecPerKm / 60);
  const s = Math.round(paceSecPerKm % 60);
  return `${m}:${String(s).padStart(2, "0")} /km`;
}

export function formatElevation(meters: number, system: UnitSystem): string {
  if (system === "imperial") return `${Math.round(meters * M_TO_FT)} ft`;
  return `${Math.round(meters)} m`;
}

export function formatSpeed(mps: number, system: UnitSystem): string {
  if (system === "imperial") return `${(mps * MPS_TO_MPH).toFixed(1)} mph`;
  return `${(mps * MPS_TO_KPH).toFixed(1)} km/h`;
}

export function convertDistance(meters: number, system: UnitSystem): number {
  if (system === "imperial") return meters / 1000 * KM_TO_MI;
  return meters / 1000;
}

export function convertElevation(meters: number, system: UnitSystem): number {
  if (system === "imperial") return meters * M_TO_FT;
  return meters;
}

export function convertSpeed(mps: number, system: UnitSystem): number {
  if (system === "imperial") return mps * MPS_TO_MPH;
  return mps * MPS_TO_KPH;
}

export function distanceLabel(system: UnitSystem): string {
  return system === "imperial" ? "mi" : "km";
}

export function elevationLabel(system: UnitSystem): string {
  return system === "imperial" ? "ft" : "m";
}

export function speedLabel(system: UnitSystem): string {
  return system === "imperial" ? "mph" : "km/h";
}

export function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}
